import API from '@api';
import FiveStarRating from '@components/FiveStarRating';
import ConfirmModal from '@components/Modals/ConfirmModal';
import EditReviewModal from '@components/Modals/EditReviewModal';
import LoadingSkeleton from '@components/LoadingSkeleton';
import useWindowSize from '@customHooks/useWindowSize';
import { userNoAvatar } from '@images';
import { createImageErrorHandler } from '@utils/onImageError';
import { getRatingText } from '@utils/ratingText';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import ReviewAttachedFiles from '../components/ReviewAttachedFiles';
import ReviewControlBtns from '../components/ReviewControlBtns';
import ReviewReply from '../components/ReviewReply';
import type {
  CommentsItemsFragmentFragment,
  ReviewsItemsFragmentFragment
} from '@schema/cart';
import { CommentsInstanceTypeEnum } from '@schema/cart';
import dayjs from 'dayjs';
import './index.scss';

const getDate = (date: string): string => dayjs(date).format('DD MMMM YYYY ');

const onImageSrcErrorHandler = createImageErrorHandler(userNoAvatar);

interface ReviewCardProps {
  message: ReviewsItemsFragmentFragment | CommentsItemsFragmentFragment;
  reviewType: 'ProductReview' | 'StoreReview';
  isCanEdit?: boolean;
  id?: string;
  onEditOrDeleteReviewCallback?: () => void;
  onSuccessfulRateAnswer?: () => void;
}

export const ReviewCardLoading: FC = () => (
  <div className='ReviewCard'>
    <div className='ReviewCard__user flex'>
      <LoadingSkeleton className='ReviewCard__user-photo-wrapper !rounded-full' />
      <div className='ReviewCard__user-info flex-1'>
        <LoadingSkeleton className='ReviewCard__user-name h-[1lh] w-1/4' />
        <div className='ReviewCard__date-and-rating-wrapper'>
          <LoadingSkeleton className='ReviewCard__date h-[1lh] w-1/6' />
        </div>
      </div>
    </div>
    <div className='ReviewCard__review-info'>
      <div className={`ReviewCard__tags`}>
        <LoadingSkeleton className='ReviewCard__tag w-[100px]' />
        <LoadingSkeleton className='ReviewCard__tag w-[140px]' />
      </div>
      <p className='ReviewCard__review-text flex flex-col gap-2'>
        <LoadingSkeleton className='h-[1lh] w-3/4' />
        <LoadingSkeleton className='h-[1lh] w-2/3' />
      </p>
    </div>
  </div>
);

export const ReviewCard: FC<ReviewCardProps> = ({
  message,
  id: idProps,
  reviewType,
  isCanEdit: isCanEditProps,
  onEditOrDeleteReviewCallback,
  onSuccessfulRateAnswer
}) => {
  const [isEditReviewModalOpened, setIsEditReviewModalOpened] = useState(false);
  const [isConfirmModalOpened, setIsConfirmModalOpened] = useState(false);
  const { isMobile } = useWindowSize();
  const reviewDate = message?.date ? getDate(message?.date) : '';

  const id = idProps || message?.id;
  const answer = message?.answer;
  const answerDate = message?.answer_date;
  const answerUserItem = message?.answerUserItem;
  const reviewId = message?.id;
  const authorId = message?.people_id;
  const userPhoto =
    message?.peopleItem?.attachmentsItem?.cacheItem?.[0]?.filePath ||
    userNoAvatar;

  const userName = message.fio || '';
  const reviewText = message.text;
  const userReviewRating = message.rating;
  const reviewTags = 'tagList' in message ? message.tagList : [];
  const userAttachedFiles = message.photos || [];
  const isCanRateAnswer = message.isCanRateAnswer;
  const isCanEdit =
    typeof isCanEditProps === 'boolean' ? isCanEditProps : message.isCanEdit;

  const answerRating = message?.answer_rating;

  const addText = useMemo(
    () => ({ __html: reviewText?.replace(/(?:\r\n|\r|\n)/g, '<br />') || '' }),
    [reviewText]
  );

  const userAvatar = useMemo(() => userPhoto || userNoAvatar, [userPhoto]);

  const deleteReviewHandler = useCallback(() => {
    const data = {
      objectId: +reviewId,
      instance: CommentsInstanceTypeEnum.Reviews
    };
    API.editPeopleReviewById({ data, toDelete: true })
      .then(() => {
        if (onEditOrDeleteReviewCallback) {
          onEditOrDeleteReviewCallback();
        }
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        setIsConfirmModalOpened(false);
      });
  }, [reviewId, onEditOrDeleteReviewCallback]);

  const onCloseConfirmModal = useCallback(() => {
    setIsConfirmModalOpened(false);
  }, []);
  const onDeleteReviewClickHandler = useCallback(() => {
    setIsConfirmModalOpened(true);
  }, []);

  const onCloseEditReviewModal = useCallback(() => {
    setIsEditReviewModalOpened(false);
  }, []);
  const openEditReviewModal = useCallback(() => {
    setIsEditReviewModalOpened(true);
  }, []);
  const onSuccessFulSubmit = useCallback(() => {
    onEditOrDeleteReviewCallback?.();
    setIsEditReviewModalOpened(false);
  }, [onEditOrDeleteReviewCallback, setIsEditReviewModalOpened]);
  return (
    <div className='ReviewCard'>
      <div className='ReviewCard__user'>
        {userAvatar && (
          <div className='ReviewCard__user-photo-wrapper'>
            <img
              onError={onImageSrcErrorHandler}
              src={userAvatar}
              alt='Изображение'
              className='ReviewCard__user-photo'
            />
          </div>
        )}
        <div className='ReviewCard__user-info'>
          <p className='ReviewCard__user-name'>{userName || 'Пользователь'}</p>
          <div className='ReviewCard__date-and-rating-wrapper'>
            {reviewDate && <div className='ReviewCard__date'>{reviewDate}</div>}
            {!!userReviewRating && (
              <div className='ReviewCard__rating'>
                <FiveStarRating
                  className='ReviewCard__five-star-rating'
                  rating={userReviewRating}
                />
                {!isMobile &&
                  reviewType !== 'ProductReview' &&
                  getRatingText(userReviewRating)}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='ReviewCard__review-info'>
        {!!reviewTags?.length && (
          <div
            className={`ReviewCard__tags${
              reviewText?.length === 0 || reviewText === ' '
                ? ' ReviewCard__tags--no-margin'
                : ''
            }`}
          >
            {reviewTags.map((item, index: number) => (
              <div
                key={item.id}
                className={`ReviewCard__tag${
                  index + 1 === reviewTags.length
                    ? ' ReviewCard__tag--last'
                    : ''
                }`}
              >
                {item.title}
              </div>
            ))}
          </div>
        )}
        {reviewText && (
          <p
            className='ReviewCard__review-text'
            dangerouslySetInnerHTML={addText}
          />
        )}
        {!!userAttachedFiles?.length && (
          <ReviewAttachedFiles attachedFiles={userAttachedFiles} />
        )}
        {/* расскомментить редактирование отзывов */}
        {isCanEdit && (
          <ReviewControlBtns
            answer={answer}
            openEditReviewModal={openEditReviewModal}
            onDeleteReviewClickHandler={onDeleteReviewClickHandler}
          />
        )}
        {answer && (
          <ReviewReply
            messageUserId={authorId}
            reviewId={reviewId}
            answer={answer}
            answerDate={answerDate}
            answerUserAvatar={
              answerUserItem?.attachmentsItem?.cacheItem?.[0]?.filePath
            }
            answerUserFio={answerUserItem?.fio}
            isCanRateAnswer={isCanRateAnswer}
            onSuccessfulRateAnswer={onSuccessfulRateAnswer}
            reviewType={reviewType}
            answerRating={answerRating}
          />
        )}
      </div>

      {isConfirmModalOpened && (
        <ConfirmModal
          open={isConfirmModalOpened}
          title='Удалить отзыв?'
          onClose={onCloseConfirmModal}
          onConfirm={deleteReviewHandler}
        />
      )}
      {isEditReviewModalOpened && (
        <EditReviewModal
          open={isEditReviewModalOpened}
          onCloseModal={onCloseEditReviewModal}
          onSuccessFulSubmit={onSuccessFulSubmit}
          reviewType='shop'
          rating={userReviewRating}
          text={reviewText}
          tags={reviewTags}
          id={id}
          attachedFiles={userAttachedFiles}
          isOpened={isEditReviewModalOpened}
        />
      )}
    </div>
  );
};
